import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const GRID_SIZE = 25;

const fairMultiplier = (revealed, minesCount) => {
  const safe = GRID_SIZE - minesCount;
  let m = 1;
  for (let i = 0; i < revealed; i++) m *= (GRID_SIZE - i) / (safe - i);
  return m;
};

const MinesPage = () => {
  const navigate = useNavigate();
  const [isUser, setIsUser] = useState(false);
  const [userCoins, setUserCoins] = useState(0);
  const [settings, setSettings] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);

  const [betAmount, setBetAmount] = useState(10);
  const [minesCount, setMinesCount] = useState(3);
  const [lastBet, setLastBet] = useState(null);

  const [roundId, setRoundId] = useState(null);
  const [revealed, setRevealed] = useState([]);
  const [minePositions, setMinePositions] = useState(null); // only known once round ends
  const [hitTile, setHitTile] = useState(null);
  const [multiplier, setMultiplier] = useState(1);
  const [potentialWin, setPotentialWin] = useState(0);
  const [resultBanner, setResultBanner] = useState(null); // { result, amount }
  const [loading, setLoading] = useState(false);
  const [tilesBusy, setTilesBusy] = useState(false);

  const isActive = !!roundId && !resultBanner;

  useEffect(() => {
    const token = localStorage.getItem('userToken');
    setIsUser(!!token);
    if (!token) {
      setTimeout(() => navigate('/login'), 1500);
      return;
    }
    axios.get('/api/games/settings/mines').then((r) => setSettings(r.data)).catch(() => {});
    axios.get('/api/users/me/coins', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setUserCoins(r.data.coins || 0)).catch(() => {});
    axios.get('/api/users/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setEmailVerified(r.data.emailVerified || r.data.email_verified || false)).catch(() => {});
  }, [navigate]);

  const syncBalance = useCallback((coins) => {
    setUserCoins(coins);
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    userData.coins = coins;
    localStorage.setItem('userData', JSON.stringify(userData));
    window.dispatchEvent(new Event('userTokenChanged'));
  }, []);

  if (!settings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-dark">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  const minBet = settings.minBet || settings.min_bet || 10;
  const maxBet = settings.maxBet || settings.max_bet || 250;
  const minMines = settings.minMines || 1;
  const maxMines = settings.maxMines || 24;
  const rtp = settings.rtp || 0.97;
  const safeTiles = GRID_SIZE - minesCount;
  const maxMultiplier = fairMultiplier(safeTiles, minesCount) * rtp;

  const clampBet = (value) => {
    let v = parseInt(value) || minBet;
    if (v < minBet) v = minBet;
    if (v > maxBet) v = maxBet;
    if (v > userCoins) v = userCoins;
    return v;
  };

  const startRound = async () => {
    if (!isUser) { navigate('/login'); return; }
    if (!emailVerified) { alert('Oyun oynamak için e-posta adresinizi doğrulamanız gerekmektedir.'); return; }
    if (betAmount > userCoins) { alert('Yetersiz puan!'); return; }

    setLoading(true);
    setResultBanner(null);
    setMinePositions(null);
    setHitTile(null);
    setRevealed([]);
    try {
      const token = localStorage.getItem('userToken');
      const { data } = await axios.post('/api/games/mines/start', { betAmount, minesCount }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoundId(data.roundId);
      setMultiplier(1);
      setPotentialWin(0);
      setLastBet({ betAmount, minesCount });
      syncBalance(data.newBalance);
    } catch (error) {
      alert(error.response?.data?.error || 'Oyun başlatılamadı');
    } finally {
      setLoading(false);
    }
  };

  const revealTile = async (index) => {
    if (!isActive || tilesBusy || revealed.includes(index)) return;
    setTilesBusy(true);
    try {
      const token = localStorage.getItem('userToken');
      const { data } = await axios.post('/api/games/mines/reveal', { roundId, tileIndex: index }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.gameOver) {
        setMinePositions(data.minePositions);
        setHitTile(data.hitTile);
        setRevealed(data.revealed);
        setResultBanner({ result: 'lose', amount: betAmount });
        syncBalance(data.newBalance);
      } else {
        setRevealed(data.revealed);
        setMultiplier(data.multiplier);
        setPotentialWin(data.potentialWin);
      }
    } catch (error) {
      alert(error.response?.data?.error || 'İşlem başarısız');
    } finally {
      setTilesBusy(false);
    }
  };

  const cashOut = async () => {
    if (!isActive || revealed.length === 0 || tilesBusy) return;
    setTilesBusy(true);
    try {
      const token = localStorage.getItem('userToken');
      const { data } = await axios.post('/api/games/mines/cashout', { roundId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMinePositions(data.minePositions);
      setMultiplier(data.multiplier);
      setResultBanner({ result: 'win', amount: data.winAmount });
      syncBalance(data.newBalance);
    } catch (error) {
      alert(error.response?.data?.error || 'Nakite çevrilemedi');
    } finally {
      setTilesBusy(false);
    }
  };

  const newGame = () => {
    setRoundId(null);
    setRevealed([]);
    setMinePositions(null);
    setHitTile(null);
    setResultBanner(null);
    setMultiplier(1);
    setPotentialWin(0);
  };

  const repeatLast = () => {
    if (!lastBet) return;
    setBetAmount(clampBet(lastBet.betAmount));
    setMinesCount(Math.min(maxMines, Math.max(minMines, lastBet.minesCount)));
  };

  return (
    <div className="min-h-screen bg-gradient-dark py-4 sm:py-8 px-2 sm:px-4 overflow-x-hidden">
      <style>{`
        @keyframes mines-pop {
          0% { transform: scale(0.5) rotate(-8deg); opacity: 0; }
          60% { transform: scale(1.12) rotate(3deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        .animate-mines-pop { animation: mines-pop 0.35s cubic-bezier(0.22,1,0.36,1) both; }
        @keyframes mines-shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
        .animate-mines-shake { animation: mines-shake 0.4s ease-in-out; }
        .mines-tile { aspect-ratio: 1 / 1; }
      `}</style>

      <div className="max-w-6xl mx-auto w-full">
        <div className="mb-4 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => navigate('/games')}
            className="inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/90 shadow-sm backdrop-blur-md transition hover:border-gold/40 hover:bg-white/10 hover:text-gold"
          >
            <span className="text-lg leading-none">‹</span>
            <span>Saguex Originals</span>
          </button>
          <div className="pulse-glow w-full rounded-2xl border border-gold/35 bg-gradient-to-br from-gold/15 to-amber-900/20 px-5 py-3 shadow-lg backdrop-blur-sm sm:w-auto">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold/80">Bakiye</div>
            <div className="text-center font-bold tabular-nums text-gold sm:text-left text-lg sm:text-xl">
              {userCoins.toLocaleString('tr-TR')} <span className="text-sm font-semibold text-gold/70">Sagu Coin</span>
            </div>
          </div>
        </div>

        {isUser && !emailVerified && (
          <div className="bg-yellow-500/20 border-2 border-yellow-500/50 rounded-xl p-4 mb-6">
            <p className="text-yellow-400 font-semibold">⚠️ Oyun oynamak için e-posta adresinizi doğrulamanız gerekmektedir.</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-8">
          {/* Control Panel */}
          <div className="order-2 lg:order-1 lg:col-span-1">
            <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-navy-800/90 to-navy-900/95 p-4 shadow-2xl shadow-black/40 backdrop-blur-md sm:rounded-3xl sm:p-6 ring-1 ring-gold/20">
              {!isActive ? (
                <>
                  <div className="mb-5 text-center">
                    <h3 className="text-lg font-bold tracking-tight text-gold">Bahis</h3>
                    <p className="mt-1 text-xs text-white/55">Mayın sayısını seçin, bahsinizi girin</p>
                  </div>

                  <div className="mb-5">
                    <label className="mb-2 block text-center text-[11px] font-semibold uppercase tracking-widest text-white/45">
                      Tutar (Sagu Coin)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={betAmount}
                        onChange={(e) => setBetAmount(clampBet(e.target.value))}
                        className="min-w-0 flex-1 rounded-xl border border-white/10 bg-black/35 px-4 py-3 text-center text-lg font-semibold tabular-nums text-white shadow-inner outline-none ring-gold/30 transition focus:border-gold/50 focus:ring-2"
                        min={minBet}
                        max={Math.min(maxBet, userCoins)}
                      />
                      <div className="flex shrink-0 flex-col gap-2">
                        <button type="button" onClick={() => setBetAmount(clampBet(Math.floor(betAmount / 2)))} className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 transition hover:bg-white/10">÷2</button>
                        <button type="button" onClick={() => setBetAmount(clampBet(betAmount * 2))} className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 transition hover:bg-white/10">×2</button>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button type="button" onClick={() => setBetAmount(minBet)} className="flex-1 rounded-xl border border-gold/25 bg-gold/10 py-2.5 text-xs font-semibold text-gold transition hover:bg-gold/20">Min</button>
                      <button type="button" onClick={() => setBetAmount(clampBet(maxBet))} className="flex-1 rounded-xl border border-gold/25 bg-gold/10 py-2.5 text-xs font-semibold text-gold transition hover:bg-gold/20">Max</button>
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-widest text-white/45">
                      <span>Mines</span>
                      <span>Taş Sayısı</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-6 text-center text-sm font-bold text-gold">{minesCount}</span>
                      <input
                        type="range"
                        min={minMines}
                        max={maxMines}
                        value={minesCount}
                        onChange={(e) => setMinesCount(parseInt(e.target.value))}
                        className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-white/10 accent-gold"
                      />
                      <span className="w-6 text-center text-sm font-bold text-gold">{safeTiles}</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={startRound}
                    disabled={loading || betAmount > userCoins || betAmount < minBet}
                    className="w-full rounded-2xl bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-emerald-900/40 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? 'Başlatılıyor…' : 'Bahis yap'}
                  </button>
                  {betAmount > userCoins && <p className="mt-3 text-center text-sm text-red-400/90">Yetersiz puan</p>}

                  <div className="mt-5 flex items-center justify-between text-xs text-white/50">
                    <button type="button" onClick={repeatLast} disabled={!lastBet} className="underline decoration-dotted underline-offset-2 hover:text-gold disabled:opacity-40 disabled:cursor-not-allowed">Tekrarla</button>
                    <span>Maks Çarpan <span className="font-semibold text-gold">{maxMultiplier.toFixed(2)}×</span></span>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-5 rounded-2xl border border-white/10 bg-black/25 p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-white/45">Güncel Çarpan</div>
                    <div className="mt-1 text-3xl font-bold tabular-nums text-gold">{multiplier.toFixed(2)}×</div>
                    <div className="mt-1 text-sm text-white/60">Nakite çevir: <span className="font-semibold text-white">{potentialWin}</span> Sagu Coin</div>
                  </div>

                  <button
                    type="button"
                    onClick={cashOut}
                    disabled={revealed.length === 0 || tilesBusy || !!resultBanner}
                    className="w-full rounded-2xl border border-gold/40 bg-gradient-to-r from-amber-400 via-gold to-amber-500 py-4 text-sm font-bold uppercase tracking-wide text-navy-900 shadow-lg shadow-amber-900/30 transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Nakite Çevir
                  </button>

                  <div className="mt-4 rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-center text-xs text-white/70">
                    <span className="text-white/50">Bahis </span>
                    <span className="font-medium text-white/85">{betAmount}</span>
                    <span className="text-white/40"> · Mayın </span>
                    <span className="font-medium text-white/85">{minesCount}</span>
                  </div>
                </>
              )}

              {resultBanner && (
                <button
                  type="button"
                  onClick={newGame}
                  className="mt-4 w-full rounded-2xl border border-gold/40 bg-gradient-to-r from-amber-400 via-gold to-amber-500 py-3.5 text-sm font-bold uppercase tracking-wide text-navy-900 shadow-lg shadow-amber-900/30 transition hover:brightness-105"
                >
                  Yeni oyun
                </button>
              )}
            </div>
          </div>

          {/* Grid */}
          <div className="order-1 lg:order-2 lg:col-span-3">
            <div
              className="relative flex min-h-[420px] flex-col overflow-hidden rounded-3xl border-2 border-gold/45 p-4 shadow-2xl shadow-black/50 sm:p-6 lg:p-8"
              style={{
                backgroundColor: '#0d3b2c',
                backgroundImage: `
                  radial-gradient(ellipse 120% 80% at 50% -20%, rgba(212, 175, 55, 0.12), transparent 55%),
                  radial-gradient(ellipse 90% 60% at 80% 100%, rgba(0, 0, 0, 0.35), transparent 50%)
                `
              }}
            >
              {!isActive && !resultBanner && (
                <div className="mb-6 flex justify-center">
                  <img
                    src="/images/originals/mines.png"
                    alt="Mines"
                    className="h-40 sm:h-48 w-auto rounded-xl object-contain shadow-[0_0_40px_rgba(212,175,55,0.25)]"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
              {resultBanner && (
                <div className={`mb-5 text-center`}>
                  <div className={`inline-block px-8 py-3 rounded-xl ${resultBanner.result === 'win' ? 'bg-green-500/20 border-2 border-green-500' : 'bg-red-500/20 border-2 border-red-500'}`}>
                    <div className={`text-xl font-bold ${resultBanner.result === 'win' ? 'text-green-400' : 'text-red-400'}`}>
                      {resultBanner.result === 'win' ? `+${resultBanner.amount} Sagu Coin Kazandınız!` : `Mayına Bastınız · -${resultBanner.amount} Sagu Coin`}
                    </div>
                  </div>
                </div>
              )}
              <div className="mx-auto grid w-full max-w-xl grid-cols-5 gap-2 sm:gap-3">
                {Array.from({ length: GRID_SIZE }).map((_, index) => {
                  const isSafeRevealed = revealed.includes(index);
                  const isMineRevealed = minePositions && minePositions.includes(index);
                  const isHit = hitTile === index;
                  const showFace = isSafeRevealed || isMineRevealed;
                  return (
                    <button
                      key={index}
                      type="button"
                      disabled={!isActive || tilesBusy || showFace}
                      onClick={() => revealTile(index)}
                      className={`
                        mines-tile relative flex items-center justify-center rounded-lg sm:rounded-xl border-2 text-xl sm:text-3xl transition-all duration-150
                        ${showFace ? '' : 'hover:-translate-y-0.5 active:translate-y-0 active:scale-95'}
                        ${isHit ? 'animate-mines-shake border-red-400 bg-gradient-to-br from-red-600 to-red-900' :
                          isMineRevealed ? 'border-red-500/40 bg-gradient-to-br from-red-900/60 to-black/60 opacity-70' :
                          isSafeRevealed ? 'animate-mines-pop border-gold bg-gradient-to-br from-gold/40 to-amber-700/40 shadow-[0_0_20px_rgba(212,175,55,0.5)]' :
                          isActive ? 'cursor-pointer border-white/10 bg-gradient-to-br from-navy-600/80 to-navy-800/90 hover:border-gold/50 hover:shadow-[0_0_15px_rgba(212,175,55,0.25)]' :
                          'cursor-default border-white/10 bg-gradient-to-br from-navy-700/60 to-navy-900/70 opacity-70'}
                      `}
                    >
                      {isMineRevealed || isHit ? (
                        <img
                          src="/images/originals/bomb.png"
                          alt=""
                          className="h-[65%] w-[65%] object-contain drop-shadow-[0_0_10px_rgba(239,68,68,0.6)]"
                        />
                      ) : isSafeRevealed ? (
                        <img
                          src="/images/originals/diamond.png"
                          alt=""
                          className="h-[65%] w-[65%] object-contain drop-shadow-[0_0_10px_rgba(16,185,129,0.6)]"
                        />
                      ) : (
                        ''
                      )}
                    </button>
                  );
                })}
              </div>
              {!isActive && !resultBanner && (
                <div className="mt-6 text-center text-sm text-white/50">Başlamak için soldan bahsinizi seçip "Bahis yap"a basın.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MinesPage;
